import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import type { AdvancedRadiusSettings } from './type/sp-advanced-radius-settings.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { SpInputNumComponent } from '../sp-form-controls/sp-input-num/sp-input-num.component';

type Corner = keyof AdvancedRadiusSettings;

/** Only the four corners travel: a caller's extra keys (`isIndependentCorners`) are never echoed back. */
const pickCorners = (settings: Partial<AdvancedRadiusSettings>): AdvancedRadiusSettings => ({
    borderTopLeftRadius: settings.borderTopLeftRadius,
    borderTopRightRadius: settings.borderTopRightRadius,
    borderBottomLeftRadius: settings.borderBottomLeftRadius,
    borderBottomRightRadius: settings.borderBottomRightRadius,
});

@Component({
    selector: 'sp-advanced-radius-settings',
    templateUrl: './sp-advanced-radius-settings.component.html',
    styleUrls: ['./sp-advanced-radius-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpInputNumComponent],
})
export class SpAdvancedRadiusSettingsComponent {
    public readonly radiusSettings = input<AdvancedRadiusSettings | null>(null);
    public readonly valueChangeEvent = output<AdvancedRadiusSettings>();

    public readonly BORDER_RADIUS_MIN = CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    public readonly BORDER_RADIUS_MAX = CSS_RESTRICTIONS.BORDER_RADIUS_MAX;

    private readonly corners = signal<AdvancedRadiusSettings>(pickCorners({}));

    public readonly borderTopLeftRadiusValue = computed(() => this.corners().borderTopLeftRadius);
    public readonly borderTopRightRadiusValue = computed(() => this.corners().borderTopRightRadius);
    public readonly borderBottomLeftRadiusValue = computed(() => this.corners().borderBottomLeftRadius);
    public readonly borderBottomRightRadiusValue = computed(() => this.corners().borderBottomRightRadius);

    constructor() {
        effect(() => {
            const incoming = this.radiusSettings();

            if (incoming) {
                this.corners.set(pickCorners(incoming));
            }
        });
    }

    public setBorderTopLeftRadius(value: number): void {
        this.setCorner('borderTopLeftRadius', value);
    }

    public setBorderTopRightRadius(value: number): void {
        this.setCorner('borderTopRightRadius', value);
    }

    public setBorderBottomLeftRadius(value: number): void {
        this.setCorner('borderBottomLeftRadius', value);
    }

    public setBorderBottomRightRadius(value: number): void {
        this.setCorner('borderBottomRightRadius', value);
    }

    private setCorner(corner: Corner, value: number): void {
        this.corners.update((corners) => ({ ...corners, [corner]: value }));
        this.valueChangeEvent.emit(this.corners());
    }
}
